// EP-161 §2.5 (D6): mark a car that left without paying, or a fake slip, as
// fraud. The server rejects the kiosk payment with the reason and records the
// plate; nothing is deleted. Owner, admin and staff (the page gates the
// button; the route keeps the accountant out).
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Button, Modal, Textarea } from '@/components/ui'
import { useMarkParkingSessionFraud } from '@/hooks/useParkingSessions'
import { useNotificationStore } from '@/stores/useNotificationStore'

const KNOWN_ERRORS = ['reason_required', 'already_flagged', 'already_paid', 'not_checked_out'] as const
type KnownError = (typeof KNOWN_ERRORS)[number]

function errorCodeOf(err: unknown): string | null {
  const data = (err as { response?: { data?: { error?: unknown } } }).response?.data
  const error = data?.error
  if (error && typeof error === 'object' && 'code' in error && typeof error.code === 'string') return error.code
  return null
}

function isKnownError(code: string | null): code is KnownError {
  return code != null && (KNOWN_ERRORS as readonly string[]).includes(code)
}

export interface FraudMarkModalProps {
  sessionId: number
  licensePlate: string
  isOpen: boolean
  onClose: () => void
}

export function FraudMarkModal({ sessionId, licensePlate, isOpen, onClose }: FraudMarkModalProps) {
  const { t } = useTranslation('parking')
  const { t: tc } = useTranslation('common')
  const addToast = useNotificationStore((s) => s.addToast)
  const markFraud = useMarkParkingSessionFraud()
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  const close = () => {
    setReason('')
    setError(null)
    onClose()
  }

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError(t('sessions.errors.reason_required'))
      return
    }
    setError(null)
    try {
      await markFraud.mutateAsync({ id: sessionId, reason: reason.trim() })
      addToast({ type: 'success', message: t('sessions.fraud_modal.success') })
      close()
    } catch (err) {
      const code = errorCodeOf(err)
      setError(isKnownError(code) ? t(`sessions.errors.${code}`) : tc('errors.something_went_wrong'))
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={t('sessions.fraud_modal.title')}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {tc('actions.cancel')}
          </Button>
          <Button
            variant="error"
            onClick={handleSubmit}
            loading={markFraud.isPending}
            disabled={!reason.trim()}
          >
            {t('sessions.fraud_modal.submit')}
          </Button>
        </>
      }
    >
      <p className="font-mono font-semibold mb-2">{licensePlate}</p>
      <p className="text-sm text-base-content/70 mb-4">{t('sessions.fraud_modal.body')}</p>
      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}
      <Textarea
        label={t('sessions.fraud_modal.reason_label')}
        rows={3}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        required
      />
    </Modal>
  )
}
